import { type components } from '@libraries/typescript/api_client';

type Organization = components['schemas']['OrganizationRead'];

export const PRE_ONBOARDING_LIFECYCLE_STAGE = 'pre_onboarding';
export const PROVISIONING_LIFECYCLE_STAGE = 'provisioning';
export const CHURNED_LIFECYCLE_STAGE = 'churned';

export function isPreOnboardingOrganization(
    organization: Organization | null | undefined,
): boolean {
    return (
        organization?.customer_lifecycle_stage ===
        PRE_ONBOARDING_LIFECYCLE_STAGE
    );
}

export function isProvisioningOrganization(
    organization: Organization | null | undefined,
): boolean {
    // The provisioning stage ships in a separate backend PR, so the generated
    // CustomerLifecycleStage union does not include it yet; widen to string so
    // the comparison typechecks until the API types are regenerated.
    const stage: string | null | undefined =
        organization?.customer_lifecycle_stage;
    return stage === PROVISIONING_LIFECYCLE_STAGE;
}

export function isChurnedOrganization(
    organization: Organization | null | undefined,
): boolean {
    const stage: string | null | undefined =
        organization?.customer_lifecycle_stage;
    return stage === CHURNED_LIFECYCLE_STAGE;
}

// Whether the organization is still in the constrained onboarding flow: the
// pre-onboarding checklist or the post-setup provisioning brief.
export function isOnboardingStageOrganization(
    organization: Organization | null | undefined,
): boolean {
    return (
        isPreOnboardingOrganization(organization) ||
        isProvisioningOrganization(organization)
    );
}

// Whether the current user should be limited to the constrained onboarding
// workspace (pre-onboarding and provisioning stages). Phoebe platform admins
// (e.g. team@phoebe.work) are never gated so they can verify data syncing and
// do implementation work on the full product before the customer is onboarded.
export function isOnboardingRestricted(
    organization: Organization | null | undefined,
    isPlatformAdmin: boolean | null | undefined,
): boolean {
    return isOnboardingStageOrganization(organization) && !isPlatformAdmin;
}

// Whether the current user should be limited to the deactivated account page.
// Phoebe platform admins are never gated so they can verify data and support
// the organization after it has churned.
export function isChurnedRestricted(
    organization: Organization | null | undefined,
    isPlatformAdmin: boolean | null | undefined,
): boolean {
    return isChurnedOrganization(organization) && !isPlatformAdmin;
}
